/**
 * /dashboard/posh/icc — ICC committee management (STRICT 2FA).
 *
 * Seat management (add / edit / deactivate / reactivate / re-appoint /
 * external email) is EMPLOYER-only (company_admin / hr_manager — POSH Act
 * §4(1); the backend 403s anyone else). A seated ICC member sees the
 * roster and the minutes but not those controls. 2026-10: the minutes are
 * shown ONLY to a member in office (the server 403s anyone else, HR
 * included — §16); a member whose term ended is badged "Term ended" and
 * can be re-appointed (§4(3)).
 *
 * Migration note (2026-06-01, Next.js → Vite, Phase 3d): port of
 * frontend/src/app/(dashboard)/dashboard/posh/icc/page.tsx.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import {
  is2FARequiredError,
  useDeactivateICCMember,
  useICCMembers,
  useUpdateICCMember,
} from "@/hooks/use-posh";
import { fmtDate, iccMemberLabel } from "@/lib/posh-format";
import {
  ICC_SEAT_STATUS_LABEL,
  canEditTerm,
  iccSeatStatus,
  termExceedsMax,
  todayISO,
} from "@/lib/posh-access";
import { ICCReappointDialog } from "@/components/posh/icc-reappoint-dialog";
import { ICCTermDialog } from "@/components/posh/icc-term-dialog";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { ICCMemberForm } from "@/components/posh/icc-member-form";
import { ICCExternalEmailDialog } from "@/components/posh/icc-external-email-dialog";
import { ICCCompositionCard } from "@/components/posh/icc-composition-card";
import { ICCMinutesSection } from "@/components/posh/icc-minutes-section";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/icc",
)({
  component: ICCRoute,
});

function MembersSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function MembersList({ canManage }: { canManage: boolean }) {
  const q = useICCMembers();
  const deactivateMut = useDeactivateICCMember();
  const updateMut = useUpdateICCMember();
  const [deactivateId, setDeactivateId] = useState<number | null>(null);

  if (q.isLoading) return <MembersSkeleton />;

  if (q.isError && is2FARequiredError(q.error)) {
    return <TwoFactorRequired surface="ICC committee management" />;
  }
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load ICC members</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }

  const members = q.data?.members ?? [];
  if (members.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No ICC members yet. Add the presiding officer and members to
          constitute the committee (POSH Act s.4).
        </CardContent>
      </Card>
    );
  }

  async function onDeactivate(id: number) {
    try {
      await deactivateMut.mutateAsync(id);
      toast.success("ICC member deactivated.");
      setDeactivateId(null);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function onReactivate(id: number) {
    try {
      await updateMut.mutateAsync({ id, body: { is_active: true } });
      toast.success("ICC member reactivated.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <div className="space-y-3">
      {members.map((m) => (
        <Card key={m.id}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base">
                  {iccMemberLabel(m)}
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {m.is_external ? "External" : "Internal"} · Appointed{" "}
                  {fmtDate(m.appointed_date)}
                  {m.term_end_date
                    ? ` · Term ends ${fmtDate(m.term_end_date)}`
                    : ""}
                  {m.is_external && m.external_email
                    ? ` · ${m.external_email}`
                    : ""}
                </p>
                {m.is_external && m.is_active && !m.external_email && (
                  <p className="mt-1 text-xs text-amber-800">
                    No notice email — this member is not told about new
                    complaints.
                  </p>
                )}
                {termExceedsMax(m) && (
                  <p className="mt-1 text-xs text-amber-800">
                    This term runs past three years from the nomination
                    (POSH Act 2013 §4(3)) — shorten it.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canManage && m.is_external && m.is_active && (
                  <ICCExternalEmailDialog member={m} />
                )}
                {canManage && canEditTerm(m) && <ICCTermDialog member={m} />}
                <StatusBadge
                  tone={
                    iccSeatStatus(m) === "in_office"
                      ? "success"
                      : iccSeatStatus(m) === "inactive"
                        ? "neutral"
                        : "warning"
                  }
                  label={ICC_SEAT_STATUS_LABEL[iccSeatStatus(m)]}
                />
                {/* §4(3): a lapsed term is re-appointed with a new term,
                    never merely "reactivated" (that would not restore
                    office). */}
                {canManage &&
                  (iccSeatStatus(m) === "term_ended" ||
                    (!m.is_active &&
                      !!m.term_end_date &&
                      m.term_end_date.slice(0, 10) < todayISO())) && (
                    <ICCReappointDialog member={m} />
                  )}
                {!canManage ? null : m.is_active ? (
                  <AlertDialog
                    open={deactivateId === m.id}
                    onOpenChange={(open) =>
                      setDeactivateId(open ? m.id : null)
                    }
                  >
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={deactivateMut.isPending}
                      >
                        Deactivate
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="sm:max-w-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Deactivate {iccMemberLabel(m)}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Deactivating a member can drop the committee below
                          the POSH Act §4 quorum, blocking complaint
                          handling. Make sure the committee stays validly
                          constituted before you do this.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel disabled={deactivateMut.isPending}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          // Keep the dialog open while the mutation runs;
                          // onDeactivate closes it on success.
                          onClick={(e) => {
                            e.preventDefault();
                            void onDeactivate(m.id);
                          }}
                          disabled={deactivateMut.isPending}
                          className={cn(
                            buttonVariants({ variant: "destructive" }),
                            "gap-1.5",
                          )}
                        >
                          {deactivateMut.isPending && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          Deactivate
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : m.term_end_date &&
                  m.term_end_date.slice(0, 10) < todayISO() ? null : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={updateMut.isPending}
                    onClick={() => void onReactivate(m.id)}
                  >
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ICCRoute() {
  const { user } = useAuth();
  const canManage = isHRAdmin(user?.role);
  const membersQ = useICCMembers();
  // Minutes are the Internal Committee's case file: shown to a member in
  // office only (the server 403s everyone else — HR included).
  const holdsSeat = (membersQ.data?.members ?? []).some(
    (m) => m.user_id != null && m.user_id === user?.user_id && iccSeatStatus(m) === "in_office",
  );
  return (
    <PoshModuleShell
      title="Internal Committee"
      description="Internal Complaints Committee composition. This area requires two-factor authentication."
      actions={canManage ? <ICCMemberForm quiet={membersQ.isError && is2FARequiredError(membersQ.error)} /> : undefined}
    >
      <PoshFeatureGate>
        <div className="space-y-6">
          {/* The §4 composition check is the employer's dashboard
              (GET /posh/icc/composition is company_admin / hr_manager
              only) — a committee member would only get a 403. */}
          {canManage && <ICCCompositionCard />}
          <MembersList canManage={canManage} />
          {/* Whether the viewer holds a seat is known only once the roster
              loads; before that (loading, or behind the 2FA gate the
              roster already shows) say nothing about the minutes. */}
          {membersQ.isSuccess && (
            <ICCMinutesSection canView={holdsSeat} canManage={canManage} />
          )}
        </div>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
